import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { render, unmountComponentAtNode } from 'react-dom';
import { Dialog } from './Dialog';

type Props = {
  title?: string;
  children?: React.ReactNode;
  body?: string;
  /** Cancel button text */
  cancel?: string;
  /** Confirm button text */
  confirm?: string;
  /** Loading label text (defaults to confirm text) */
  loadingLabel?: string;
  onCancel: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  onConfirm: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  show?: boolean;
  danger?: boolean;
  isLoading?: boolean;
};

export const Confirm = ({
  title = 'Discard changes?',
  children,
  body = 'Are you sure you want to close this form and discard your changes?',
  cancel = 'Stay with form',
  confirm = 'Discard',
  loadingLabel,
  show = true,
  onCancel,
  onConfirm,
  danger,
  isLoading = false,
}: Props) => {
  return (
    <Dialog
      show={show}
      title={title}
      onClose={onCancel}
      danger={danger}
      isLoading={isLoading}
    >
      <div className='mx-4 my-8 text-center text-sm font-normal text-copy-alt'>
        {children ?? body}
      </div>
      <div className='m-4 text-right'>
        <button
          className={classNames(
            'text-xs uppercase transition-all duration-300',
            isLoading && 'disabled:opacity-50'
          )}
          onClick={(e) => onCancel(e)}
          disabled={isLoading}
        >
          {cancel}
        </button>
        <button
          data-usetiful='confirm-button'
          className={classNames(
            'ml-8 rounded-md p-2 text-xs uppercase text-white shadow transition-all duration-300',
            danger ? 'bg-delete' : 'bg-brand',
            isLoading && 'disabled:opacity-50'
          )}
          onClick={(e) => onConfirm(e)}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className='flex items-center justify-center gap-2'>
              <svg
                className='h-4 w-4 animate-spin'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'
                ></circle>
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                ></path>
              </svg>
              <span>{loadingLabel ?? confirm}</span>
            </div>
          ) : (
            confirm
          )}
        </button>
      </div>
    </Dialog>
  );
};

type ConfirmMessageProps = Omit<
  Props,
  'children' | 'onConfirm' | 'onCancel'
> & {
  onConfirm: (message: string) => void;
  onCancel: () => void;
};

export function ConfirmMessage({
  body,
  onConfirm,
  onCancel,
  ...props
}: ConfirmMessageProps) {
  const ref = useRef<HTMLTextAreaElement>(null);

  return (
    <Confirm
      {...props}
      onCancel={onCancel}
      onConfirm={() => onConfirm(ref.current?.value ?? '')}
    >
      {body}
      <textarea
        ref={ref}
        className='input mt-3 w-full resize-none rounded-md border border-grey-20 px-3 py-2.5 focus-visible:border-brand focus-visible:outline-none'
        placeholder='Add optional message...'
        autoFocus
      ></textarea>
    </Confirm>
  );
}

export function useConfirm({ when = true }: { when?: boolean } = {}) {
  const confirmRef = useRef<HTMLDivElement>();

  useEffect(() => {
    const confirmRoot = document.createElement('div');
    document.body.appendChild(confirmRoot);
    confirmRef.current = confirmRoot;

    return () => {
      document.body.removeChild(confirmRoot);
    };
  }, []);

  const confirm = (fn: CallableFunction) => () => {
    if (!when) {
      return fn();
    }

    if (!confirmRef.current) {
      return;
    }

    render(
      <Confirm
        onCancel={() => {
          if (confirmRef.current) {
            unmountComponentAtNode(confirmRef.current);
          }
        }}
        onConfirm={() => {
          if (confirmRef.current) {
            unmountComponentAtNode(confirmRef.current);
          }
          fn();
        }}
      />,
      confirmRef.current
    );
  };

  return { confirm };
}
